import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { EditIcon } from "../comps/CustomIcons";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { postRequest } from '../services/api.request';

const EXCLUDE_LABEL = [
  {
    id: 1,
    label: "Category",
    value: "category",
  },
  {
    id: 2,
    label: "Name",
    value: "name",
  },
  {
    id: 3,
    label: "Item Name",
    value: "itemName",
  },
  {
    id: 4,
    label: "",
    value: "",
  },
];

const EXCLUDE_RELIEF_LABEL = [
  {
    id: 1,
    label: "Relief Name",
    value: "reliefName",
  },
  {
    id: 2,
    label: "Exclude From",
    value: "excludeFrom",
  },
  {
    id: 3,
    label: "Percentage",
    value: "percentage",
  },
  {
    id: 4,
    label: "Exclude Global Package & Package Item",
    value: "excludeGlobalPackageItem",
  },
  {
    id: 5,
    label: "",
    value: "",
  },
];

const MasterSharingTab = ({ template = {}, onChange = () => {}, readOnly = false }) => {
  // Initialize states with empty values first
  const [formData, setFormData] = useState({
    templateName: '',
    priority: '',
    selfDiscount: 'afterSelfDiscount',
    sharingBasePrice: '',
    referDoctor: '',
    userName: '',
  });
  const [excludedDepartments, setExcludedDepartments] = useState([]);
  const [excludedReliefDepartments, setExcludedReliefDepartments] = useState([]);
  const [checkboxStates, setCheckboxStates] = useState({
    active: false,
    applySharingOnGlobalComboPackage: false,
    calculateSharingWithoutTaxForNonInventoryItems: false,
    selectAll: false
  });

  // Initialize form data with template values
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;
    
    if (template) {
      setFormData({
        templateName: template.templateName || '',
        priority: template.priority || '',
        selfDiscount: template.selfDiscount || 'afterSelfDiscount',
        sharingBasePrice: template.sharingBasePrice || '',
        referDoctor: template.referDoctor || '',
        userName: template.userName || ''
      });
      setExcludedDepartments(template.excludedDepartments || []);
      setExcludedReliefDepartments(template.excludedReliefDepartments || []);
      setCheckboxStates({
        active: template.active || false,
        applySharingOnGlobalComboPackage: template.applySharingOnGlobalComboPackage || false,
        calculateSharingWithoutTaxForNonInventoryItems: template.calculateSharingWithoutTaxForNonInventoryItems || false,
        selectAll: template.selectAll || false
      });
    } else {
      // Initialize with empty values if no template provided
      setFormData({
        templateName: '',
        priority: '',
        selfDiscount: 'afterSelfDiscount',
        sharingBasePrice: '',
        referDoctor: '',
        userName: ''
      });
      setExcludedDepartments([]);
      setExcludedReliefDepartments([]);
      setCheckboxStates({
        active: false,
        applySharingOnGlobalComboPackage: false,
        calculateSharingWithoutTaxForNonInventoryItems: false,
        selectAll: false
      });
    }
    
    setIsInitialized(true);
  }, [template]);

  // Add navigate hook
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Prepare the data to be sent
    const dataToSave = {
      ...formData,
      ...checkboxStates,
      excludedDepartments,
      excludedReliefDepartments
    };

    try {
      // Send data to the server using our API service
      const response = await postRequest('/sharing-templates/v1', dataToSave);
      
      if (response && response.success) {
        // Show success toast
        toast.success('Template saved successfully!');
        // Navigate back to sharing master page
        navigate('/master/sharing', { replace: true });
      } else {
        // Handle API error response
        const errorMessage = response?.message || 'Failed to save template';
        toast.error(errorMessage);
      }
    } catch (error) {
      // Handle network or other errors
      const errorMessage = error.message || 'Failed to connect to server';
      toast.error(errorMessage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = {
      ...formData,
      [name]: value,
    };
    setFormData(newData);
    // Only update parent with changed field
    onChange({
      ...template,
      [name]: value
    });
  };

  const handleRadioChange = (event) => {
    const newData = {
      ...formData,
      selfDiscount: event.target.value,
    };
    setFormData(newData);
    onChange(newData);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckboxStates(prev => ({
      ...prev,
      [name]: checked
    }));
    // Update only the changed checkbox state
    onChange({
      ...template,
      [name]: checked
    });
  };

  const handleAddTableRow = (currentElement, currentState, newRowData) => {
    currentState((prevState) => [...prevState, newRowData]);
  };

  const handleRemoveTableRow = (rowIndex, currentElement, currentState) => {
    currentState((prevState) => prevState.filter((_, i) => i !== rowIndex));
  };

  const handleInputChange = (event, rowIndex, currentState) => {
    const { name, value } = event.target;
    currentState((prevState) =>
      prevState.map((row, i) =>
        i === rowIndex ? { ...row, [name]: value } : row
      )
    );
  };

  return (
    <Box>
      {/* first box */}
      <Box
        sx={{
          backgroundColor: "rgba(240, 247, 252, 1)",
          borderRadius: "12px",
          mb: "30px",
          p: "20px",
        }}
      >
        <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
          Basic Details
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {/* template name */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Template Name"
              name="templateName"
              required
              fullWidth
              sx={{ bgcolor: "white" }}
              value={formData.templateName}
              onChange={handleChange}
              slotProps={{ input: { readOnly } }}
            />
          </Grid>
          {/* priority */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Priority"
              name="priority"
              type="number"
              required
              fullWidth
              sx={{ bgcolor: "white" }}
              value={formData.priority}
              onChange={handleChange}
              slotProps={{ input: { readOnly } }}
            />
          </Grid>
          {/* active */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={checkboxStates.active}
                  onChange={handleCheckboxChange}
                  disabled={readOnly}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Box>

      {/* second box */}
      <Box
        sx={{
          backgroundColor: "rgba(240, 247, 252, 1)",
          borderRadius: "12px",
          mb: "30px",
          p: "20px",
        }}
      >
        <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
          Sharing Consultation on Amount
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {/* template name */}
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="after discount"
                name="selfDiscount"
                row
                value={formData.selfDiscount}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value="afterSelfDiscount"
                  control={<Radio disabled={readOnly} />}
                  label="After Self Discount"
                />
                <FormControlLabel
                  value="afterTotalDiscount"
                  control={<Radio disabled={readOnly} />}
                  label="After Total Discount"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {/* applySharingOnGlobalComboPackage */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="applySharingOnGlobalComboPackage"
                  checked={checkboxStates.applySharingOnGlobalComboPackage}
                  onChange={handleCheckboxChange}
                  disabled={readOnly}
                />
              }
              label="Apply Sharing On Global Combo Package"
            />
          </Grid>
          {/* calculateSharingWithoutTaxForNonInventoryItems */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="calculateSharingWithoutTaxForNonInventoryItems"
                  checked={checkboxStates.calculateSharingWithoutTaxForNonInventoryItems}
                  onChange={handleCheckboxChange}
                  disabled={readOnly}
                />
              }
              label="Calculate Sharing Without Tax For Non Inventory Items"
            />
          </Grid>

          {/* sharingBasePrice */}
          <Grid item xs={12} sm={6} xl={4}>
            <Autocomplete
              options={[]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sharing Base Price"
                  name="sharingBasePrice"
                  value={formData.sharingBasePrice}
                  onChange={handleChange}
                  slotProps={{ input: { readOnly } }}
                />
              )}
              sx={{ bgcolor: "white" }}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      </Box>

      {/* third box */}
      <Box
        sx={{
          backgroundColor: "rgba(240, 247, 252, 1)",
          borderRadius: "12px",
          mb: "30px",
          p: "20px",
        }}
      >
        <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
          Select User
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {/* selectAll */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="selectAll"
                  checked={checkboxStates.selectAll}
                  onChange={handleCheckboxChange}
                />
              }
              label="Select All"
            />
          </Grid>
          {/* referDoctor */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={[]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Refer Doctor"
                  name="referDoctor"
                  value={formData.referDoctor}
                  onChange={handleChange}
                />
              )}
              sx={{ bgcolor: "white" }}
            />
          </Grid>

          {/* userName */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={[]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User Name"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                />
              )}
              sx={{ bgcolor: "white" }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* fourth box */}
      <Box
        sx={{
          border: `1px solid #CCD4DA`,
          borderRadius: "12px",
          mb: "30px",
          p: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography>Exclude</Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                bgcolor: "#EAEAEA",
              }}
            >
              <TableRow>
                {EXCLUDE_LABEL?.map((option) => (
                  <TableCell
                    key={option?.id}
                    sx={{
                      textAlign: option?.align || "left",
                      color: "#5D6A82",
                    }}
                  >
                    {option?.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {excludedDepartments?.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {EXCLUDE_LABEL?.map((column, columnIndex) => (
                    <TableCell
                      key={`${rowIndex}${columnIndex}`}
                      sx={{ color: "#5D6A82" }}
                    >
                      {columnIndex === EXCLUDE_LABEL?.length - 1 ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <EditIcon
                            sx={{
                              cursor: "pointer",
                              color: "transparent",
                              fontSize: "18px",
                            }}
                          />
                          <Close
                            sx={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() =>
                              handleRemoveTableRow(
                                rowIndex,
                                excludedDepartments,
                                setExcludedDepartments
                              )
                            }
                          />
                        </Box>
                      ) : (
                        <TextField
                          label={column?.label}
                          required
                          fullWidth
                          name={column?.value}
                          value={row[column?.value] || ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              rowIndex,
                              setExcludedDepartments
                            )
                          }
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  colSpan={EXCLUDE_LABEL?.length}
                  sx={{ color: "#92A6CC", cursor: "pointer" }}
                  onClick={() =>
                    handleAddTableRow(
                      excludedDepartments,
                      setExcludedDepartments,
                      {}
                    )
                  }
                >
                  + Add item
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* fifth box */}
      <Box
        sx={{
          border: `1px solid #CCD4DA`,
          borderRadius: "12px",
          mb: "30px",
          p: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography>Exclude Relief Criteria</Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                bgcolor: "#EAEAEA",
              }}
            >
              <TableRow>
                {EXCLUDE_RELIEF_LABEL?.map((option) => (
                  <TableCell
                    key={option?.id}
                    sx={{
                      textAlign: option?.align || "left",
                      color: "#5D6A82",
                    }}
                  >
                    {option?.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {excludedReliefDepartments?.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {EXCLUDE_RELIEF_LABEL?.map((column, columnIndex) => (
                    <TableCell
                      key={`${rowIndex}${columnIndex}`}
                      sx={{ color: "#5D6A82" }}
                    >
                      {columnIndex === EXCLUDE_RELIEF_LABEL?.length - 1 ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <EditIcon
                            sx={{
                              cursor: "pointer",
                              color: "transparent",
                              fontSize: "18px",
                            }}
                          />
                          <Close
                            sx={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() =>
                              handleRemoveTableRow(
                                rowIndex,
                                excludedReliefDepartments,
                                setExcludedReliefDepartments
                              )
                            }
                          />
                        </Box>
                      ) : (
                        <TextField
                          label={column?.label}
                          required
                          fullWidth
                          name={column?.value}
                          value={row[column?.value] || ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              rowIndex,
                              setExcludedReliefDepartments
                            )
                          }
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  colSpan={EXCLUDE_RELIEF_LABEL?.length}
                  sx={{ color: "#92A6CC", cursor: "pointer" }}
                  onClick={() =>
                    handleAddTableRow(
                      excludedReliefDepartments,
                      setExcludedReliefDepartments,
                      {}
                    )
                  }
                >
                  + Add item
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Submit Button - Only show if in standalone mode and not readonly */}
      {!template && !readOnly && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="contained"
            sx={{ minWidth: "150px" }}
            onClick={(e) => {
              handleSubmit(e);
            }}
          >
            Submit
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default MasterSharingTab;
